import { LedgerAccount } from "@/api"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

/** How an account reads in a picker: `522400 — Qty discrepancy`. */
export function ledger_account_label(account: LedgerAccount): string {
  return [account.account_number, account.expense_account].filter(Boolean).join(" — ")
}

/** Sentinel for "nothing selected": Radix Select cannot hold an empty string value. */
export const LEDGER_ACCOUNT_NONE = "__none__"

interface LedgerAccountSelectProps {
  /** The selected account's number, or null when unset. */
  value: string | null
  /** Called with the account number, or null when the "none" option is picked. */
  onValueChange: (account_number: string | null) => void
  accounts: LedgerAccount[]
  /** Label for the "nothing selected" option. Omit to require a choice. */
  none_label?: string
  placeholder?: string
  className?: string
  id?: string
}

/**
 * Pick one GL account from the org's chart of accounts, by account number.
 *
 * Settings that name an account store its number, and a number typed by hand is a silent failure —
 * nothing matches it, so nothing codes. This lists what the org actually has.
 */
export function LedgerAccountSelect({
  value,
  onValueChange,
  accounts,
  none_label,
  placeholder = "Select GL account",
  className,
  id,
}: LedgerAccountSelectProps) {
  return (
    <Select
      value={value ?? LEDGER_ACCOUNT_NONE}
      onValueChange={(selected: string) => onValueChange(selected === LEDGER_ACCOUNT_NONE ? null : selected)}>
      <SelectTrigger id={id} class={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {none_label && <SelectItem value={LEDGER_ACCOUNT_NONE}>{none_label}</SelectItem>}
        {accounts
          .filter((account: LedgerAccount) => account.account_number)
          .map((account: LedgerAccount) => (
            <SelectItem key={account.id} value={account.account_number!}>
              {ledger_account_label(account)}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}
